import {
  INTL_DMY_CASUAL_FORMAT,
  INTL_SHORT_TIME_FORMAT_AM_PM,
  INTL_SHORT_MONTH_LONG_YEAR,
} from 'constants/dateFormats'
import type { IntlShape } from 'react-intl'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

export const REQUEST_FLIGHT_SUPPORT_CALLBACK_PATH = 'flightsupportcallback'
export const REQUEST_SUPPORT_CALLBACK_PATH = 'supportcallback'
export const REQUEST_SALES_CALLBACK_PATH = 'salescallback'
export const REQUEST_TOURS_SALES_CALLBACK_PATH = 'tourscallback'
export const REQUEST_TOURS_CONCIERGE_CALLBACK_PATH = 'toursconciergecallback'

type DateFormatter = (
  date: Date | string | undefined,
  instruction: Intl.DateTimeFormatOptions,
) => string

export const formatCallbackTimeLabel = (
  time: string | undefined,
  dateFormatter: DateFormatter,
  fallbackLabel?: string,
): string => {
  if (!time) {
    return fallbackLabel ?? ''
  }

  const [hoursString, minutesString] = time.split(':')
  const hours = Number(hoursString)
  const minutes = Number(minutesString)

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return fallbackLabel ?? time
  }

  const timeDate = new Date()
  timeDate.setHours(hours, minutes, 0, 0)

  return dateFormatter(timeDate, INTL_SHORT_TIME_FORMAT_AM_PM)
}

export const parseSelectedTravelDatesToFormText = (filters: App.OfferListFilters, intl: IntlShape): string => {
  const anytimeText = intl.formatMessage({
    id: 'IK_LiXm4JsNVIkA-wNv-',
    defaultMessage: 'Anytime',
    description: 'Default travel date text when no dates are selected',
  })

  if (!filters) {
    return anytimeText
  }

  const { flexibleMonths, flexibleNights, checkIn, checkOut } = filters
  let result = anytimeText

  if (flexibleMonths) {
    const flexibleMonthsArr = flexibleMonths.split(',')
    const formattedMonthsList = flexibleMonthsArr
      .map(month => intlDateFormatter(month, INTL_SHORT_MONTH_LONG_YEAR, intl.locale))
    const formattedMonths = intl.formatList(formattedMonthsList, { type: 'disjunction' })

    if (flexibleNights) {
      // flexibleNights is a range string like '1-3', '4-7' — always plural
      result = intl.formatMessage(
        {
          id: 'PKJhCKP7LRBe2yFIU9MQ',
          defaultMessage: '{nights} nights, {months}',
          description: 'Travel dates text showing night range and flexible months',
        },
        { nights: flexibleNights, months: formattedMonths },
      )
    } else {
      result = formattedMonths
    }
  } else if (checkIn && checkOut) {
    result = intl.formatMessage(
      {
        id: '2LQRrBMFZPJJGBo-hv1M',
        defaultMessage: '{checkInDate} - {checkOutDate}',
        description: 'Travel date range showing check-in to check-out dates',
      },
      {
        checkInDate: intlDateFormatter(checkIn, INTL_DMY_CASUAL_FORMAT, intl.locale),
        checkOutDate: intlDateFormatter(checkOut, INTL_DMY_CASUAL_FORMAT, intl.locale),
      },
    )
  }

  return result
}

export const parseSelectedNumberOfTravellersToFormText = (filters: App.OfferListFilters, intl: IntlShape) => {
  if (!filters) {
    return ''
  }
  const result = filters?.rooms?.reduce(
    (totals, room) => ({
      totalAdults: totals.totalAdults + (room.adults || 0),
      totalChildren: totals.totalChildren + (room.children || 0),
    }),
    { totalAdults: 0, totalChildren: 0 },
  ) || { totalAdults: 0, totalChildren: 0 }

  // changing this to "one"/"other" instead of "1"/"2"+" Adults"/" Children"
  // because linter requires "one" plural rule to be present for CLDR compliance
  const adultText = result.totalAdults > 0
    ? intl.formatMessage(
        {
          id: '_WPrqGZrANfj_zIBzsKu',
          defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
          description: 'Number of adults for callback request traveller count',
        },
        { count: result.totalAdults },
      )
    : ''
  const childText = result.totalChildren > 0
    ? intl.formatMessage(
        {
          id: 'dyD5wNgjMt9PG3kuYVti',
          defaultMessage: '{count, plural, one {# Child} other {# Children}}',
          description: 'Number of children for callback request traveller count',
        },
        { count: result.totalChildren },
      )
    : ''

  return [adultText, childText].filter(Boolean).join(', ')
}
